import { useState, useCallback, useRef, useEffect } from 'react';
import {
  DSST_GAME_DURATION,
  CORRECT_FEEDBACK_MS,
  WRONG_FEEDBACK_MS,
  PRACTICE_FEEDBACK_MS,
  PRACTICE_COUNT,
  generateSymbolMap,
  shuffleNumbers,
  calculateScore,
  pickNextSymbol,
  getRandomSymbolSet,
  type QuestionRecord,
  type DsstGameData,
  type Feedback,
} from '@/pages/dsst/dsstConfig';

export type DsstScreen = 'guide' | 'practice' | 'countdown' | 'game' | 'result';

export interface PracticeRecord {
  symbolId: number;
  answer: number;
  correctAnswer: number;
  isCorrect: boolean;
}

export function useGameState() {
  const [screen, setScreen] = useState<DsstScreen>('guide');
  const [symbolMap, setSymbolMap] = useState<number[]>([]);
  const [symbolSetIndex, setSymbolSetIndex] = useState(() => getRandomSymbolSet());
  const [currentSymbol, setCurrentSymbol] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(DSST_GAME_DURATION);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [numberOrder, setNumberOrder] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [showExitModal, setShowExitModal] = useState(false);

  const symbolMapRef = useRef<number[]>([]);
  const currentSymbolRef = useRef(0);
  const questionStartRef = useRef(0);
  const endTimeRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const feedbackTimerRef = useRef<number | null>(null);
  const historyRef = useRef<number[]>([]);
  const questionsRef = useRef<QuestionRecord[]>([]);
  const correctRef = useRef(0);
  const wrongRef = useRef(0);
  const comboRef = useRef(0);
  const practiceResultsRef = useRef<PracticeRecord[]>([]);
  const isAnsweringRef = useRef(false);
  const isActiveRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (feedbackTimerRef.current !== null) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const setCurrentSymbolBoth = useCallback((s: number) => {
    currentSymbolRef.current = s;
    setCurrentSymbol(s);
  }, []);

  const finishGame = useCallback(
    (aborted = false) => {
      clearTimers();
      isActiveRef.current = false;
      isAnsweringRef.current = false;

      const correct = correctRef.current;
      const wrong = wrongRef.current;
      const total = correct + wrong;
      const finalScore = calculateScore(correct, wrong);
      const questions = questionsRef.current;

      const avgReaction =
        questions.length > 0
          ? Math.round(
              questions.reduce((s, q) => s + q.reactionTimeMs, 0) / questions.length,
            )
          : 0;

      // 기호별 정답률 / 평균 반응시간 집계
      const symbolAccuracy: Record<number, { correct: number; total: number; rate: number }> = {};
      const symbolSumRt: Record<number, { sum: number; count: number }> = {};
      for (let i = 0; i < 6; i++) {
        symbolAccuracy[i] = { correct: 0, total: 0, rate: 0 };
        symbolSumRt[i] = { sum: 0, count: 0 };
      }
      questions.forEach((q) => {
        symbolAccuracy[q.symbolId].total += 1;
        if (q.isCorrect) symbolAccuracy[q.symbolId].correct += 1;
        symbolSumRt[q.symbolId].sum += q.reactionTimeMs;
        symbolSumRt[q.symbolId].count += 1;
      });
      for (let i = 0; i < 6; i++) {
        const a = symbolAccuracy[i];
        a.rate = a.total > 0 ? Math.round((a.correct / a.total) * 1000) / 10 : 0;
      }
      const symbolAvgReactionMs: Record<number, number> = {};
      for (let i = 0; i < 6; i++) {
        const s = symbolSumRt[i];
        symbolAvgReactionMs[i] = s.count > 0 ? Math.round(s.sum / s.count) : 0;
      }

      const gameData: DsstGameData = {
        gameType: 'dsst',
        performedAt: new Date().toISOString(),
        totalQuestions: total,
        correctCount: correct,
        wrongCount: wrong,
        accuracy: total > 0 ? Math.round((correct / total) * 1000) / 10 : 0,
        questions,
        averageReactionTimeMs: avgReaction,
        symbolAccuracy,
        symbolAvgReactionMs,
        score: finalScore,
        isAborted: aborted,
      };

      setScore(finalScore);
      setTotalQuestions(total);
      setCorrectCount(correct);
      setWrongCount(wrong);
      setRemainingSeconds(0);
      console.log('DSST 연습 데이터:', practiceResultsRef.current);
      console.log('DSST 본게임 데이터:', gameData);
      setScreen('result');
    },
    [clearTimers],
  );

  const startTimer = useCallback(() => {
    endTimeRef.current = Date.now() + DSST_GAME_DURATION * 1000;
    setRemainingSeconds(DSST_GAME_DURATION);
    timerRef.current = window.setInterval(() => {
      const remaining = endTimeRef.current - Date.now();
      if (remaining <= 0) {
        setRemainingSeconds(0);
        finishGame();
      } else {
        setRemainingSeconds(Math.ceil(remaining / 1000));
      }
    }, 100);
  }, [finishGame]);

  const showNextQuestion = useCallback(() => {
    const next = pickNextSymbol(historyRef.current);
    historyRef.current = [...historyRef.current, next];
    setCurrentSymbolBoth(next);
    setSelectedNumber(null);
    setFeedback(null);
    // 본게임에서만 숫자 패널 위치를 랜덤으로 변경 (연습은 고정 순서 유지)
    if (isActiveRef.current) {
      setNumberOrder(shuffleNumbers());
    }
    isAnsweringRef.current = false;
    questionStartRef.current = Date.now();
  }, [setCurrentSymbolBoth]);

  const goToPractice = useCallback(() => {
    clearTimers();
    isActiveRef.current = false;
    const map = generateSymbolMap();
    symbolMapRef.current = map;
    setSymbolMap(map);
    practiceResultsRef.current = [];
    setPracticeIndex(0);
    historyRef.current = [];
    const first = pickNextSymbol([]);
    historyRef.current = [first];
    setCurrentSymbolBoth(first);
    setSelectedNumber(null);
    setFeedback(null);
    isAnsweringRef.current = false;
    questionStartRef.current = Date.now();
    setScreen('practice');
  }, [clearTimers, setCurrentSymbolBoth]);

  const handlePracticeSelect = useCallback(
    (number: number) => {
      if (isAnsweringRef.current) return;
      isAnsweringRef.current = true;

      const map = symbolMapRef.current;
      const symbolId = currentSymbolRef.current;
      const correctAnswer = map[symbolId];
      const isCorrect = number === correctAnswer;

      practiceResultsRef.current = [
        ...practiceResultsRef.current,
        { symbolId, answer: number, correctAnswer, isCorrect },
      ];

      setSelectedNumber(number);
      setFeedback(isCorrect ? 'correct' : 'wrong');

      feedbackTimerRef.current = window.setTimeout(() => {
        if (practiceResultsRef.current.length < PRACTICE_COUNT) {
          setPracticeIndex(practiceResultsRef.current.length);
          showNextQuestion();
        } else {
          setScreen('countdown');
        }
      }, PRACTICE_FEEDBACK_MS);
    },
    [showNextQuestion],
  );

  const goToGame = useCallback(() => {
    clearTimers();
    const map = generateSymbolMap();
    symbolMapRef.current = map;
    setSymbolMap(map);
    historyRef.current = [];
    questionsRef.current = [];
    correctRef.current = 0;
    wrongRef.current = 0;
    comboRef.current = 0;
    setCombo(0);
    setCorrectCount(0);
    setWrongCount(0);
    setTotalQuestions(0);
    setNumberOrder([1, 2, 3, 4, 5, 6]);
    const first = pickNextSymbol([]);
    historyRef.current = [first];
    setCurrentSymbolBoth(first);
    setSelectedNumber(null);
    setFeedback(null);
    isAnsweringRef.current = false;
    isActiveRef.current = true;
    questionStartRef.current = Date.now();
    setScreen('game');
    startTimer();
  }, [clearTimers, setCurrentSymbolBoth, startTimer]);

  const handleGameSelect = useCallback(
    (number: number) => {
      if (!isActiveRef.current || isAnsweringRef.current) return;
      if (Date.now() >= endTimeRef.current) return; // 제한시간 초과 시 입력 차단
      isAnsweringRef.current = true;

      const map = symbolMapRef.current;
      const symbolId = currentSymbolRef.current;
      const correctAnswer = map[symbolId];
      const isCorrect = number === correctAnswer;
      const reactionTimeMs = Date.now() - questionStartRef.current;

      const record: QuestionRecord = {
        symbolId,
        answer: number,
        correctAnswer,
        isCorrect,
        reactionTimeMs,
      };
      questionsRef.current = [...questionsRef.current, record];

      if (isCorrect) {
        correctRef.current += 1;
        setCorrectCount(correctRef.current);
        // 연속 정답(Combo) 카운트 증가 — 지속 노출
        comboRef.current += 1;
        setCombo(comboRef.current);
      } else {
        wrongRef.current += 1;
        setWrongCount(wrongRef.current);
        comboRef.current = 0; // 오답 시 Combo 초기화
        setCombo(0);
      }
      setTotalQuestions(questionsRef.current.length);

      setSelectedNumber(number);
      setFeedback(isCorrect ? 'correct' : 'wrong');

      feedbackTimerRef.current = window.setTimeout(() => {
        showNextQuestion();
      }, isCorrect ? CORRECT_FEEDBACK_MS : WRONG_FEEDBACK_MS);
    },
    [showNextQuestion],
  );

  const openExitModal = useCallback(() => setShowExitModal(true), []);
  const closeExitModal = useCallback(() => setShowExitModal(false), []);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    clearTimers();
    isActiveRef.current = false;
    isAnsweringRef.current = false;
    comboRef.current = 0;
    setCombo(0);
    setSymbolSetIndex(getRandomSymbolSet());
    setScreen('guide');
  }, [clearTimers]);

  const goToNext = useCallback(() => {
    // TODO: 다음 단계(음성 인지건강 체크)로 이동
  }, []);

  return {
    screen,
    symbolMap,
    symbolSetIndex,
    currentSymbol,
    selectedNumber,
    feedback,
    practiceIndex,
    remainingSeconds,
    score,
    totalQuestions,
    correctCount,
    wrongCount,
    combo,
    numberOrder,
    showExitModal,
    goToPractice,
    handlePracticeSelect,
    goToGame,
    handleGameSelect,
    openExitModal,
    closeExitModal,
    confirmExit,
    goToNext,
  };
}